/**
 * 7/24 Lastik — Conversion Engine
 * Tracking, A/B Testing, Heatmap — unified system
 */

const API = '';

// ─── SESSION ───
function getSessionId() {
  let sid = sessionStorage.getItem('724_session');
  if (!sid) {
    sid = 'sess_' + Math.random().toString(36).substr(2, 12) + '_' + Date.now();
    sessionStorage.setItem('724_session', sid);
  }
  return sid;
}

function getDevice() {
  const w = window.innerWidth;
  if (w < 640) return 'mobile';
  if (w < 1024) return 'tablet';
  return 'desktop';
}

// ─── EVENT TRACKING ───
async function trackEvent(eventName, page, metadata = {}) {
  try {
    const variants = getABVariants();
    await fetch(`${API}/api/event`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        event_name: eventName,
        page: page || window.location.pathname,
        device: getDevice(),
        source: document.referrer ? 'referral' : 'direct',
        session_id: getSessionId(),
        variant: variants ? JSON.stringify(variants) : null,
        metadata: { ...metadata, ...variants },
      }),
    });
  } catch (e) {
    console.warn('[724Engine] Event tracking failed:', e.message);
  }
}

// ─── CTA TRACKING ───
function trackCallClick(page) {
  trackEvent('call_click', page);
}

function trackWhatsappClick(page) {
  trackEvent('whatsapp_click', page);
}

function trackLocationShare(page) {
  trackEvent('location_share', page);
}

// ─── PAGE VIEW ───
function trackPageView(page) {
  trackEvent('page_view', page);
}

// ─── SCROLL DEPTH ───
let maxScrollDepth = 0;
let scrollTrackingActive = false;

function startScrollTracking(page) {
  if (scrollTrackingActive) return;
  scrollTrackingActive = true;
  maxScrollDepth = 0;

  const handler = () => {
    const scrollTop = window.scrollY;
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    const depth = docHeight > 0 ? Math.round((scrollTop / docHeight) * 100) : 0;
    if (depth > maxScrollDepth) {
      maxScrollDepth = depth;
    }
  };

  window.addEventListener('scroll', handler, { passive: true });

  // Send scroll depth on page leave
  const sendDepth = () => {
    if (maxScrollDepth > 0) {
      trackEvent('scroll_depth', page, { depth: maxScrollDepth });
      trackHeatmap('scroll', page, 0, 0, maxScrollDepth);
    }
    window.removeEventListener('scroll', handler);
    scrollTrackingActive = false;
  };

  window.addEventListener('beforeunload', sendDepth, { once: true });

  // Also send every 30 seconds if still on page
  const interval = setInterval(() => {
    if (maxScrollDepth > 0) {
      trackHeatmap('scroll', page, 0, 0, maxScrollDepth);
    }
  }, 30000);

  return () => {
    clearInterval(interval);
    sendDepth();
  };
}

// ─── SECTION VISIBILITY ───
function trackSectionVisibility(sectionId, page) {
  if (typeof IntersectionObserver === 'undefined') return;

  const el = document.querySelector(`[data-testid="${sectionId}"]`);
  if (!el) return;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          trackEvent('section_visible', page, { section: sectionId });
          observer.unobserve(el);
        }
      });
    },
    { threshold: 0.3 }
  );
  observer.observe(el);
  return () => observer.disconnect();
}

// ─── EXIT INTENT ───
function trackExitIntent(page) {
  const handler = (e) => {
    if (e.clientY < 10) {
      trackEvent('exit_intent', page);
      document.removeEventListener('mouseout', handler);
    }
  };
  document.addEventListener('mouseout', handler);
  return () => document.removeEventListener('mouseout', handler);
}

// ─── A/B TESTING ───
let cachedVariants = null;

async function fetchABVariants() {
  try {
    const stored = localStorage.getItem('724_ab_variants');
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Date.now() - parsed._ts < 86400000) {
        cachedVariants = parsed;
        return parsed;
      }
    }

    const res = await fetch(`${API}/api/ab-variant?session_id=${getSessionId()}`);
    const data = await res.json();
    const variants = { ...data.variants, _ts: Date.now() };
    localStorage.setItem('724_ab_variants', JSON.stringify(variants));
    cachedVariants = variants;
    return variants;
  } catch (e) {
    // Fallback defaults
    return {
      hero_cta: 'Hemen Ara — Bekleme',
      cta_color: 'red',
      microcopy: 'Hemen yonleniyoruz',
      live_panel_position: 'below_hero',
    };
  }
}

function getABVariants() {
  if (cachedVariants) return cachedVariants;
  const stored = localStorage.getItem('724_ab_variants');
  if (stored) {
    cachedVariants = JSON.parse(stored);
    return cachedVariants;
  }
  return null;
}

// ─── LIVE STATUS ───
async function fetchLiveStatus() {
  try {
    const res = await fetch(`${API}/api/live-status`);
    if (!res.ok) {
      console.warn('[724Engine] Live status failed:', res.status);
      return null;
    }
    return await res.json();
  } catch (e) {
    console.warn('[724Engine] Live status fetch failed:', e.message);
    return null;
  }
}

// ─── HEATMAP TRACKING ───
async function trackHeatmap(eventType, page, x, y, scrollDepth = 0, elementId = '') {
  try {
    await fetch(`${API}/api/heatmap`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        session_id: getSessionId(),
        page: page || window.location.pathname,
        event_type: eventType,
        x, y,
        viewport_width: window.innerWidth,
        viewport_height: window.innerHeight,
        scroll_depth: scrollDepth,
        element_id: elementId,
      }),
    });
  } catch (e) {
    console.warn('[724Engine] Heatmap tracking failed:', e.message);
  }
}

function startClickTracking(page) {
  const handler = (e) => {
    const testId = e.target.closest('[data-testid]')?.getAttribute('data-testid') || '';
    trackHeatmap('click', page, e.clientX, e.clientY, 0, testId);
  };
  document.addEventListener('click', handler);
  return () => document.removeEventListener('click', handler);
}

// ─── EXPORTS ───
export {
  getSessionId,
  getDevice,
  trackEvent,
  trackCallClick,
  trackWhatsappClick,
  trackLocationShare,
  trackPageView,
  startScrollTracking,
  trackSectionVisibility,
  trackExitIntent,
  fetchABVariants,
  getABVariants,
  fetchLiveStatus,
  trackHeatmap,
  startClickTracking,
};
